import { useForm } from '@mantine/form';
import { forwardRef, useMemo } from 'react';
import { Button, Paper, Group } from '@/components/ui';
import { FormField, buildValidator, type FormFieldConfig, type FormFieldType } from '@/components/ui/forms';
import { getTranslator } from '@/localize';
import { usePreferencesStore } from '@/stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type FieldType = FormFieldType;
export type FormField = FormFieldConfig;

export interface DynamicFormProps {
  fields: FormField[];
  onSubmit: (values: Record<string, any>) => void | Promise<void>;
  initialValues?: Record<string, any>;
  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  loading?: boolean;
  columns?: 1 | 2 | 3 | 4;
  gap?: number | string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  showReset?: boolean;
  compact?: boolean;
}

const DynamicForm = forwardRef<HTMLFormElement, DynamicFormProps>(
  (
    {
      fields,
      onSubmit,
      initialValues = {},
      submitLabel = 'Submit',
      cancelLabel = 'Cancel',
      onCancel,
      loading = false,
      columns = 1,
      gap = 'md',
      gradientVariant = 'primary',
      showReset = false,
      compact = false,
    },
    ref
  ) => {
    const language = usePreferencesStore((state) => state.language);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // @ts-expect-error - Language type mismatch with getTranslator
    const t = useMemo(() => getTranslator(language), [language]);

    const defaultValues = useMemo(() => {
      const values: Record<string, any> = {};
      fields.forEach((field) => {
        values[field.name] = initialValues[field.name] ?? field.defaultValue ?? getDefaultForType(field.type);
      });
      return values;
    }, [fields, initialValues]);

    const validationRules = useMemo(() => {
      const rules: Record<string, (value: any) => string | null> = {};
      fields.forEach((field) => {
        // @ts-expect-error - Validator type mismatch
        rules[field.name] = buildValidator(field, t);
      });
      return rules;
    }, [fields, t]);

    const form = useForm({
      initialValues: defaultValues,
      validate: validationRules,
    });

    const handleSubmit = async (values: Record<string, any>) => {
      await onSubmit(values);
    };

    const gridStyle = {
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: typeof gap === 'number' ? gap : `var(--mantine-spacing-${gap})`,
    };

    return (
      <form ref={ref} onSubmit={form.onSubmit(handleSubmit)}>
        <div style={gridStyle}>
          {fields.map((field) => {
            if (field.visible && !field.visible(form.values)) return null;

            return (
              <div
                key={field.name}
                style={{
                  gridColumn: field.span ? `span ${field.span}` : undefined,
                }}
              >
                <FormField field={field} form={form} compact={compact} disabled={loading} />
              </div>
            );
          })}
        </div>

        <Group justify="flex-end" mt="xl">
          {showReset && (
            <Button
              variant="subtle"
              onClick={() => form.reset()}
              disabled={loading}
            >
              Reset
            </Button>
          )}
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </Button>
          )}
          <Button
            type="submit"
            loading={loading}
            gradientVariant={gradientVariant}
          >
            {submitLabel}
          </Button>
        </Group>
      </form>
    );
  }
);

function getDefaultForType(type: FieldType): any {
  switch (type) {
    case 'checkbox':
    case 'switch':
      return false;
    case 'multiselect':
      return [];
    case 'number':
    case 'slider':
    case 'rating':
      return 0;
    default:
      return '';
  }
}

DynamicForm.displayName = 'DynamicForm';

export default DynamicForm;
